import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  ThemeProvider,
  ToastProvider,
  useTheme,
  Button,
  Layout as UiLayout,
  Sider,
  Header,
  Content,
  Menu,
  Popover,
  Icon,
  Space,
  Text,
  type IconName,
  type MenuItem,
} from "@app/ui";
import { AuthProvider, useAuth } from "./auth/AuthContext.js";
import { DemoWriteGuard } from "./auth/DemoWriteGuard.js";
import { useCan } from "./auth/useCan.js";
import { useWebConfig } from "./config/useWebConfig.js";
import { BrandMark } from "./components/BrandMark.js";
import { ProtectedRoute } from "./components/ProtectedRoute.js";
import { LicenseBanner } from "./components/LicenseBanner.js";
import { EmailVerifyBanner } from "./components/EmailVerifyBanner.js";
import { DelinquentBanner } from "./components/DelinquentBanner.js";
import { DemoBanner } from "./components/DemoBanner.js";
import { LangProvider, useLang, useT, type MessageKey } from "./i18n.js";
import { ErrorBoundary } from "./errors/index.js";
import { OrgProvider } from "./orgs/OrgContext.js";
import { OrgSwitcher } from "./orgs/OrgSwitcher.js";
import { LoginPage } from "./pages/LoginPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { ProfilePage } from "./pages/ProfilePage.js";
import { AdminOrgsPage } from "./pages/AdminOrgsPage.js";
import { PlatformPassportsPage, PlatformProductsPage } from "./pages/PlatformCatalogPages.js";
import { PlatformOverviewPage } from "./pages/PlatformOverviewPage.js";
import { PlatformAccessBanner } from "./components/PlatformAccessBanner.js";
import { ShowDemoProvider } from "./platform/useShowDemo.js";
import { UsersPage } from "./pages/UsersPage.js";
import { AuditPage } from "./pages/AuditPage.js";
import { LicensePage } from "./pages/LicensePage.js";
import { ProductsPage } from "./pages/ProductsPage.js";
import { FacilitiesPage } from "./pages/FacilitiesPage.js";
import { ReviewsPage } from "./pages/ReviewsPage.js";
import { LandingPage } from "./pages/LandingPage.js";
import { ProductDetailPage } from "./pages/ProductDetailPage.js";
import { ImportPage } from "./pages/ImportPage.js";
import { PassportsPage } from "./pages/PassportsPage.js";
import { PassportDetailPage } from "./pages/PassportDetailPage.js";
import { OrgSettingsPage } from "./pages/OrgSettingsPage.js";
import { DisclosuresPage } from "./pages/DisclosuresPage.js";
import { AcceptInvitePage } from "./pages/AcceptInvitePage.js";
import { VerifyEmailPage } from "./pages/VerifyEmailPage.js";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage.js";
import { ResetPasswordPage } from "./pages/ResetPasswordPage.js";
import { BillingPage } from "./pages/BillingPage.js";
import { NotFoundPage } from "./pages/NotFoundPage.js";

interface NavEntry {
  key: string;
  label: MessageKey;
  icon: IconName;
  perm?: string;
}

interface NavSection {
  key: string;
  label: MessageKey;
  items: NavEntry[];
}

// Grouped by page role: what an operator does day-to-day (catalog/passport work) is
// separated from org/admin settings and personal account, each under a section header.
const NAV: NavSection[] = [
  {
    // Platform operator surface (global permissions): every tenant and every account,
    // not just the selected org. Listed first for
    // platform admins; hidden entirely for ordinary org members. SPA paths
    // live under /platform — /admin/* is proxied to the API by Caddy and Vite.
    key: "platform",
    label: "nav.section.platform",
    items: [
      { key: "/platform", label: "nav.platformOverview", icon: "grid", perm: "org:manage" },
      { key: "/platform/orgs", label: "nav.adminOrgs", icon: "building", perm: "org:manage" },
      { key: "/platform/products", label: "nav.allProducts", icon: "box", perm: "org:manage" },
      { key: "/platform/passports", label: "nav.allPassports", icon: "file-text", perm: "org:manage" },
      { key: "/users", label: "nav.users", icon: "users", perm: "user:read" },
      { key: "/audit", label: "nav.audit", icon: "eye", perm: "audit:read" },
      { key: "/license", label: "nav.license", icon: "shield", perm: "system:read" },
    ],
  },
  {
    key: "overview",
    label: "nav.section.overview",
    items: [{ key: "/dashboard", label: "nav.dashboard", icon: "grid" }],
  },
  {
    key: "catalog",
    label: "nav.section.catalog",
    items: [
      { key: "/products", label: "nav.products", icon: "box", perm: "product:read" },
      { key: "/passports", label: "nav.passports", icon: "file-text", perm: "passport:read" },
      { key: "/reviews", label: "nav.reviews", icon: "star", perm: "product:read" },
    ],
  },
  {
    key: "admin",
    label: "nav.section.admin",
    items: [
      { key: "/org", label: "nav.org", icon: "building" },
      { key: "/billing", label: "nav.billing", icon: "calendar", perm: "org:read" },
      // Facilities are org-level config (production sites, certifications), so they
      // live under Organization rather than in the day-to-day catalog section.
      { key: "/facilities", label: "nav.facilities", icon: "map-pin", perm: "product:read" },
      // Annual unsold-goods destruction disclosure (ESPR Art. 24) — a corporate statement, admin-only.
      { key: "/disclosures", label: "nav.disclosures", icon: "file-text", perm: "disclosure:manage" },
    ],
  },
  {
    key: "account",
    label: "nav.section.account",
    items: [{ key: "/profile", label: "nav.profile", icon: "user" }],
  },
];

const NAV_LEAVES: NavEntry[] = NAV.flatMap((s) => s.items);

function Shell() {
  const { user, logout } = useAuth();
  const can = useCan();
  const { billingEnabled } = useWebConfig();
  // Nav visibility: permission gate + hide billing entirely on on-prem installs.
  const navVisible = (n: NavEntry) => (n.key !== "/billing" || billingEnabled) && (!n.perm || can(n.perm));
  const { theme, toggle } = useTheme();
  const { lang, setLang } = useLang();
  const t = useT();
  const navigate = useNavigate();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobile, setMobile] = useState(false);

  // Auto-collapse the 240px rail below the lg breakpoint (992px) so it never
  // forces horizontal scroll on tablet/phone — the custom <Sider> is presentational
  // and doesn't honor `breakpoint` itself. Same 991px threshold the nav-close
  // handler below uses. Crossing the breakpoint re-syncs; a manual toggle in
  // between is preserved until the next crossing (matches antd's behavior).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 991px)");
    setCollapsed(mq.matches);
    setMobile(mq.matches);
    const onChange = (e: MediaQueryListEvent) => {
      setCollapsed(e.matches);
      setMobile(e.matches);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Below lg the rail is a drawer floating over the content (see .app-sider in
  // styles.css), so it needs modal manners: freeze the page behind it and let
  // Escape close it.
  const drawerOpen = mobile && !collapsed;
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCollapsed(true);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen]);

  const isActive = (key: string) => loc.pathname === key || loc.pathname.startsWith(`${key}/`);
  // Longest match wins: "/platform" must not light up on "/platform/orgs".
  const activeKey = NAV_LEAVES.filter(navVisible)
    .filter((n) => isActive(n.key))
    .sort((a, b) => b.key.length - a.key.length)[0]?.key;

  // Emit one antd Menu group per section, dropping sections whose items are all permission-hidden.
  const menuItems: MenuItem[] = NAV.flatMap((section) => {
    const items = section.items
      .filter(navVisible)
      .map((n) => ({ key: n.key, label: t(n.label), icon: <Icon name={n.icon} /> }));
    return items.length
      ? [{ key: section.key, type: "group" as const, label: t(section.label), children: items }]
      : [];
  });

  const go = (key: string) => {
    navigate(key);
    // On narrow screens the sider overlays content — close it after navigating.
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 991px)").matches) {
      setCollapsed(true);
    }
  };

  return (
    <div className="app-frame">
      {/* System-wide notices sit above the rail AND the header, so they read as
          chrome for the whole app instead of a message inside the content column. */}
      <div className="app-banners">
        <LicenseBanner />
        <EmailVerifyBanner />
        <DelinquentBanner />
        <PlatformAccessBanner />
        <DemoBanner />
      </div>

      {/* Tap-anywhere-else close for the mobile drawer. Only mounted while it is
          open, so no media query is needed to keep it off the desktop layout. */}
      {drawerOpen ? (
        <div className="app-scrim" aria-hidden="true" onClick={() => setCollapsed(true)} />
      ) : null}

      <UiLayout className="app-shell" hasSider>
        <Sider
          theme="dark"
          width={240}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          collapsedWidth={0}
          trigger={null}
          className={`app-sider${collapsed ? " app-sider--collapsed" : ""}`}
        >
          {/* Light rail (light mode) / dark rail (dark mode); brand mark follows the rail text. */}
          <div className="app-brand" style={{ color: "var(--sidebar-foreground)" }}>
            <BrandMark size={22} className="brand-mark" />
            IDPP
          </div>
          <Menu theme="dark" selectedKey={activeKey} onSelect={go} items={menuItems} />
        </Sider>

        {/* min-w-0: let the main column shrink below its content's intrinsic width
          (a flex child defaults to min-width:auto) so a narrow viewport never
          gets horizontal scroll once the rail has collapsed. */}
        <UiLayout className="min-w-0">
          <Header className="app-header">
            <Button
              variant="subtle"
              size="sm"
              icon={<Icon name="menu" />}
              aria-label={t("common.openNav")}
              onClick={() => setCollapsed((c) => !c)}
            />
            <span className="app-spacer" />
            <OrgSwitcher />
            <Button
              variant="subtle"
              size="sm"
              aria-label={t("common.language")}
              onClick={() => setLang(lang === "en" ? "tr" : "en")}
            >
              {lang === "en" ? "TR" : "EN"}
            </Button>
            <Button
              variant="subtle"
              size="sm"
              aria-label={t("common.toggleTheme")}
              icon={<Icon name={theme === "light" ? "moon" : "sun"} />}
              onClick={toggle}
            />
            <Popover
              trigger="click"
              placement="bottom"
              content={
                <Space direction="vertical">
                  <Text small secondary>
                    {user?.email}
                  </Text>
                  <Button
                    size="sm"
                    variant="subtle"
                    icon={<Icon name="user" size={14} />}
                    onClick={() => navigate("/profile")}
                  >
                    {t("nav.profile")}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    icon={<Icon name="log-out" size={14} />}
                    onClick={() => logout().then(() => navigate("/login"))}
                  >
                    {t("common.signOut")}
                  </Button>
                </Space>
              }
            >
              <Button variant="subtle" size="sm" icon={<Icon name="user" size={16} />}>
                <span className="app-user-email">{user?.email?.split("@")[0]}</span>
              </Button>
            </Popover>
          </Header>

          <Content className="app-content">
            <div className="content-inner">
              <Outlet />
            </div>
          </Content>
        </UiLayout>
      </UiLayout>
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <LangProvider>
          <ToastProvider>
            <AuthProvider>
              <DemoWriteGuard />
              <OrgProvider>
                <ShowDemoProvider>
                  <BrowserRouter>
                    <Routes>
                      {/* Public marketing home at the site root. The old /landing alias
                        301s to / at the edge (deploy/Caddyfile) — one canonical URL. */}
                      <Route path="/" element={<LandingPage />} />
                      {/* English home. The blog and guides live at /en/blog and
                        /en/guides and are served by the API, not by this router. */}
                      <Route path="/en" element={<LandingPage forceLang="en" />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/accept-invite" element={<AcceptInvitePage />} />
                      <Route path="/verify-email" element={<VerifyEmailPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      <Route element={<ProtectedRoute />}>
                        <Route element={<Shell />}>
                          <Route path="/dashboard" element={<DashboardPage />} />
                          <Route path="/profile" element={<ProfilePage />} />
                          <Route element={<ProtectedRoute permission="product:read" />}>
                            <Route path="/products" element={<ProductsPage />} />
                            <Route path="/products/:id" element={<ProductDetailPage />} />
                            <Route path="/facilities" element={<FacilitiesPage />} />
                            <Route path="/reviews" element={<ReviewsPage />} />
                          </Route>
                          <Route element={<ProtectedRoute permission="import:run" />}>
                            <Route path="/import" element={<ImportPage />} />
                          </Route>
                          <Route element={<ProtectedRoute permission="passport:read" />}>
                            <Route path="/passports" element={<PassportsPage />} />
                            <Route path="/passports/:id" element={<PassportDetailPage />} />
                          </Route>
                          <Route path="/org" element={<OrgSettingsPage />} />
                          <Route element={<ProtectedRoute permission="disclosure:manage" />}>
                            <Route path="/disclosures" element={<DisclosuresPage />} />
                          </Route>
                          <Route path="/billing" element={<BillingPage />} />
                          <Route element={<ProtectedRoute permission="org:manage" />}>
                            <Route path="/platform" element={<PlatformOverviewPage />} />
                            <Route path="/platform/orgs" element={<AdminOrgsPage />} />
                            <Route path="/platform/products" element={<PlatformProductsPage />} />
                            <Route path="/platform/passports" element={<PlatformPassportsPage />} />
                          </Route>
                          <Route element={<ProtectedRoute permission="user:read" />}>
                            <Route path="/users" element={<UsersPage />} />
                          </Route>
                          <Route element={<ProtectedRoute permission="audit:read" />}>
                            <Route path="/audit" element={<AuditPage />} />
                          </Route>
                          <Route element={<ProtectedRoute permission="system:read" />}>
                            <Route path="/license" element={<LicensePage />} />
                          </Route>
                        </Route>
                      </Route>
                      {/* A real 404 view (noindex) instead of bouncing to the home page:
                        a client redirect makes every typo look like a copy of / to
                        crawlers (soft 404). */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </BrowserRouter>
                </ShowDemoProvider>
              </OrgProvider>
            </AuthProvider>
          </ToastProvider>
        </LangProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
